import { cn } from "@/lib/utils";
import {
  ButtonHTMLAttributes,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";

type Variant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "magic"
  | "credit-magic"
  | "custom"
  | "subtle";
type Size = "small" | "medium" | "large";

const isMagic = (variant: Variant) =>
  variant === "magic" || variant === "credit-magic";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant: Variant;
  size: Size;
  children?: React.ReactNode;
  /** Optional icon, rendered to the left of the text at 16px. */
  icon?: React.ReactNode;
  /** When set, the button fills the full width of its parent (no horizontal padding). */
  iconPosition?: "left" | "right";
  stretch?: boolean;
  disabled?: boolean;
  /**
   * Selected state: the button reports something that is currently on, rather
   * than an action to take. Reads as a brighter fill and white text, never a
   * colour, so a row of them says which is on without implying good or bad.
   * Also exposed as data-selected, for custom buttons to target themselves.
   */
  selected?: boolean;
  /**
   * Magic variants only: the action runs. The dots drift faster
   * (magic-loading in design-system.css), the button can't be pressed and
   * doesn't fade or shrink. The caller swaps in a pulsing label.
   */
  loading?: boolean;
};

/*
  The gradient border (border-gradient utility, defined in base.css) is applied
  directly on the <button> so the button itself is the single, clickable element —
  no wrapper div. Each variant paints its interior via --border-gradient-fill and
  its 1px border from --color-<variant>-border → transparent.
*/
const variantClasses: Record<Variant, string> = {
  primary: cn(
    "border-gradient text-on-primary",
    "[--border-gradient-fill:var(--color-primary)]",
    "[--border-gradient-from:var(--color-primary-border)]",
    "[--border-gradient-to:var(--color-primary)]",
    "hover:[--border-gradient-fill:var(--color-primary-hover)]",
    "hover:[--border-gradient-from:var(--color-primary-hover)] hover:[--border-gradient-to:var(--color-primary-hover)]",
    "data-[selected=true]:[--border-gradient-fill:var(--color-primary-active)]",
    "data-[selected=true]:[--border-gradient-to:var(--color-primary-active)]",
  ),
  secondary: cn(
    "border-gradient text-on-secondary",
    "[--border-gradient-fill:var(--color-secondaryy)]",
    "[--border-gradient-from:var(--color-secondaryy-border)]",
    "[--border-gradient-to:var(--color-secondaryy)]",
    "hover:[--border-gradient-fill:var(--color-secondaryy-hover)]",
    "hover:[--border-gradient-from:var(--color-secondaryy-hover)] hover:[--border-gradient-to:var(--color-secondaryy-hover)]",
    "data-[selected=true]:text-foreground",
    "data-[selected=true]:[--border-gradient-fill:var(--color-secondaryy-hover)]",
    "data-[selected=true]:[--border-gradient-from:var(--color-edge-strongest)]",
    "data-[selected=true]:[--border-gradient-to:var(--color-secondaryy-hover)]",
  ),
  // Text only: the quiet "no thanks" beside a primary action in a dialog.
  subtle: cn(
    "text-foreground-muted underline decoration-neutral-500 underline-offset-4",
    "hover:text-foreground hover:decoration-foreground transition-colors",
  ),
  /*
    The quiet control: a chip fill with a hairline border, for a button that has
    to sit on a dark panel without competing with the primary action next to it
    — Add players, the alternatives pill, Import Club.
  */
  tertiary: cn(
    "border-gradient text-neutral-300",
    "[--border-gradient-fill:var(--color-sbc-chip)]",
    "[--border-gradient-from:var(--color-sbc-border)]",
    "[--border-gradient-to:var(--color-sbc-border)]",
    "hover:[--border-gradient-fill:var(--color-surface-raised)]",
    "data-[selected=true]:text-foreground",
    "data-[selected=true]:[--border-gradient-fill:var(--color-neutral-800)]",
    "data-[selected=true]:[--border-gradient-from:var(--color-edge-strongest)]",
    "data-[selected=true]:[--border-gradient-to:var(--color-edge-strongest)]",
  ),
  /*
    The site's key actions: a dot surface whose dots gather round the pointer
    (magic-dots in design-system.css), with a plain label.
  */
  magic: "magic-dots text-neutral-300",
  /*
    Magic for actions that cost a Credit (Build Solutions, Build Plan): the
    dots get a hint of holo. Put text-holo on the label.
  */
  "credit-magic": "magic-dots magic-holo text-neutral-300",
  /*
    Custom supplies only the structure (the gradient-border mechanism). The color
    scheme comes from the caller's className via the --border-gradient-* vars and a
    text color — e.g. "[--border-gradient-fill:var(--color-orange-800)] text-neutral-50 …".
    Written literally at the call site so Tailwind generates the classes.
  */
  custom: "border-gradient",
};

const sizeClasses: Record<Size, string> = {
  small: "h-8 text-sm",
  medium: "h-10 text-base",
  large: "h-12.5 text-xl",
};

// Horizontal padding — skipped when the button is stretched to full width.
const paddingClasses: Record<Size, string> = {
  small: "px-3",
  medium: "px-3",
  large: "px-5",
};

const roundedClasses: Record<Size, string> = {
  small: "rounded-[20px]",
  medium: "rounded-[20px]",
  large: "rounded-[30px]",
};

// Disabled overrides the variant entirely: flat disabled fill (border matches, so
// no visible gradient), muted text, no hover.
const disabledClasses = cn(
  "border-gradient text-on-disabled cursor-not-allowed",
  "[--border-gradient-fill:var(--color-disabled)]",
  "[--border-gradient-from:var(--color-disabled)]",
  "[--border-gradient-to:var(--color-disabled)]",
);
// Subtle has no fill, so disabled only greys it.
const subtleDisabledClasses = "text-on-disabled cursor-not-allowed";

const iconSizeClasses: Record<Size, string> = {
  small: "size-4 [&>svg]:size-4",
  medium: "size-5 [&>svg]:size-5",
  large: "size-6 [&>svg]:size-6",
};

// IconButton: square, icon-only. Used when no children are passed. Same height
// as the text button per size, but width == height (no horizontal text padding).
const iconButtonSizeClasses: Record<Size, string> = {
  small: "size-8",
  medium: "size-10",
  large: "size-12.5",
};

// IconButton uses a flat solid border (same style as BackButton) instead of the
// gradient border, while still tinting the fill/text per variant.
const iconButtonVariantClasses: Record<Variant, string> = {
  primary: cn(
    "border border-neutral-600 bg-[var(--color-primary)] text-on-primary",
    "hover:bg-[var(--color-primary-hover)] transition-colors",
  ),
  secondary: cn(
    "border border-neutral-600 bg-[var(--color-secondaryy)] text-on-secondary",
    "hover:bg-[var(--color-secondaryy-hover)] transition-colors",
    "data-[selected=true]:bg-[var(--color-secondaryy-hover)] data-[selected=true]:text-foreground",
  ),
  tertiary: cn(
    "border border-sbc-border bg-sbc-chip text-neutral-300",
    "hover:bg-surface-raised transition-colors",
  ),
  magic: cn(
    "border border-sbc-border bg-sbc-chip text-neutral-300",
    "hover:bg-surface-raised transition-colors",
  ),
  "credit-magic": cn(
    "border border-sbc-border bg-sbc-chip text-neutral-300",
    "hover:bg-surface-raised transition-colors",
  ),
  subtle: "text-foreground-muted hover:text-foreground transition-colors",
  // Custom supplies its own fill/text via className; only the solid border is fixed.
  custom: "border border-neutral-600 transition-colors",
};

/**
 * The icon slot's own classes, the companion to {@link buttonClasses}: a
 * non-button wearing the button skin needs the icon sized and spaced the same
 * way, and copying these two strings is how the two drift apart.
 */
export const buttonIconClasses = ({
  size = "medium",
  iconPosition = "left",
}: {
  size?: Size;
  iconPosition?: "left" | "right";
}) =>
  cn(
    "flex shrink-0 items-center justify-center",
    iconSizeClasses[size],
    iconPosition === "left" ? "mr-1" : "ml-1",
  );

/**
 * The text-button skin on its own, for the rare element that must not be a
 * <button>, such as an <a> that has to look like one, or a chip that only
 * reports a state. Kept here so the two can never drift apart.
 */
export const buttonClasses = ({
  variant = "primary",
  size = "medium",
  iconPosition = "left",
  stretch,
  disabled,
  className,
}: {
  variant?: Variant;
  size?: Size;
  iconPosition?: "left" | "right";
  stretch?: boolean;
  disabled?: boolean;
  className?: string;
}) =>
  cn(
    "flex items-center justify-center font-medium",
    // magic-dots draws its own shadow beside its border.
    variant !== "subtle" && !isMagic(variant) && "shadow-button-new",
    iconPosition === "left" ? "flex-row" : "flex-row-reverse",
    disabled
      ? variant === "subtle"
        ? subtleDisabledClasses
        : isMagic(variant)
          ? // Magic keeps its dot surface, faded (magic-dots in design-system.css).
            cn(variantClasses[variant], "magic-disabled")
          : disabledClasses
      : variantClasses[variant],
    sizeClasses[size],
    // Quieter than the buttons beside it, whatever their size.
    variant === "subtle" && "text-sm",
    roundedClasses[size],
    // Subtle reads as a text link, so it never fills the row
    stretch && variant !== "subtle"
      ? "flex w-full"
      : ["w-fit", paddingClasses[size]],
    className,
  );

// How long the magic cluster takes to fade where the pointer left, before its drift restarts.
const MAGIC_FADE_MS = 300;
// How long the cluster takes to glide over to the pointer when it first arrives (magic-dots' arriving transition).
const MAGIC_ARRIVE_MS = 500;

/**
 * The magic variants' pointer: reports where it is and what it's doing, and
 * magic-dots in design-system.css draws and eases the dots from that.
 */
const useMagicDots = (enabled: boolean) => {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const arriving = useRef<ReturnType<typeof setTimeout>>(undefined);
  const button = useRef<HTMLButtonElement>(undefined);
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      clearTimeout(arriving.current);
    },
    [],
  );
  // Disabled under the pointer (a click that starts its action): the leave never
  // arrives, so drop the cluster here, else it stays pinned and the drift never restarts.
  useEffect(() => {
    const el = button.current;
    if (enabled || !el) return;
    clearTimeout(timer.current);
    clearTimeout(arriving.current);
    el.style.removeProperty("--magic-x");
    el.style.removeProperty("--magic-y");
    delete el.dataset.magic;
    delete el.dataset.magicArriving;
  }, [enabled]);
  if (!enabled) return undefined;
  return {
    move: (e: React.PointerEvent<HTMLButtonElement>) => {
      const el = e.currentTarget;
      button.current = el;
      const rect = el.getBoundingClientRect();
      clearTimeout(timer.current);
      if (el.dataset.magic !== "tracking") {
        // Pins the cluster where it is now (mid drift or mid fade) before the
        // drift stops, so it glides over to the pointer instead of jumping.
        const from = getComputedStyle(el);
        el.style.setProperty("--magic-x", from.getPropertyValue("--magic-x"));
        el.style.setProperty("--magic-y", from.getPropertyValue("--magic-y"));
        el.dataset.magic = "tracking";
        el.dataset.magicArriving = "";
        // Flushes styles so the glide starts from the pinned spot.
        void getComputedStyle(el).getPropertyValue("--magic-x");
        clearTimeout(arriving.current);
        arriving.current = setTimeout(
          () => delete el.dataset.magicArriving,
          MAGIC_ARRIVE_MS,
        );
      }
      el.style.setProperty("--magic-x", `${e.clientX - rect.left}px`);
      el.style.setProperty("--magic-y", `${e.clientY - rect.top}px`);
    },
    leave: (e: React.PointerEvent<HTMLButtonElement>) => {
      const el = e.currentTarget;
      el.dataset.magic = "leaving";
      timer.current = setTimeout(() => {
        el.style.removeProperty("--magic-x");
        el.style.removeProperty("--magic-y");
        delete el.dataset.magic;
      }, MAGIC_FADE_MS);
    },
  };
};

// magic-dots-drift's and magic-dots-loading's sweep (12% to 88% of the width, ease-in-out), and the loading sweep's time.
const DRIFT_FROM = 0.12;
const DRIFT_SPAN = 0.76;
const LOADING_SWEEP_S = 1.2;

/**
 * Where the cluster is right now as a point in the loading sweep, as a negative
 * animation delay, so loading carries on from it instead of jumping to the start.
 */
const loadingDelay = (el: HTMLElement) => {
  const value = getComputedStyle(el).getPropertyValue("--magic-x");
  const width = el.offsetWidth;
  if (!/%|px/.test(value) || !width) return "0s";
  // A percentage, pixels (following the pointer) or a calc() of both mid-glide.
  const x =
    Number(value.match(/(-?[\d.]+)%/)?.[1] ?? 0) / 100 +
    Number(value.match(/(-?[\d.]+)px/)?.[1] ?? 0) / width;
  const progress = Math.min(1, Math.max(0, (x - DRIFT_FROM) / DRIFT_SPAN));
  // ease-in-out is cubic-bezier(0.42, 0, 0.58, 1): find the time that reaches this progress.
  const bezier = (s: number, a: number, b: number) =>
    3 * a * s * (1 - s) ** 2 + 3 * b * s ** 2 * (1 - s) + s ** 3;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    if (bezier(mid, 0, 1) < progress) lo = mid;
    else hi = mid;
  }
  const time = bezier((lo + hi) / 2, 0.42, 0.58);
  // The drift's direction carries on; a cluster following the pointer heads for the far side.
  const drift = el
    .getAnimations()
    .find(
      (a): a is CSSAnimation =>
        a instanceof CSSAnimation && a.animationName === "magic-dots-drift",
    );
  const iteration = drift?.effect?.getComputedTiming().currentIteration;
  const forward = iteration == null ? progress < 0.5 : iteration % 2 === 0;
  // alternate: odd iterations run backwards, so the way back starts one sweep in.
  return `${-(forward ? time : 2 - time) * LOADING_SWEEP_S}s`;
};

export const Button = ({
  variant = "primary",
  className,
  children,
  size = "medium",
  icon,
  iconPosition = "left",
  stretch,
  disabled,
  selected,
  loading,
  onPointerMove,
  onPointerLeave,
  onClick,
  ...props
}: ButtonProps) => {
  const magicLoading = isMagic(variant) && !!loading;
  // A magic button that can't be pressed looks disabled, aria-disabled ones too
  // (a step waiting on its own action); a loading one keeps its look.
  const magicOff =
    isMagic(variant) &&
    !magicLoading &&
    (disabled ||
      props["aria-disabled"] === true ||
      props["aria-disabled"] === "true");
  const magic = useMagicDots(isMagic(variant) && !magicOff && !magicLoading);
  // A loading magic button carries its dots on from where they were and keeps the
  // width its idle label gave it, so only the label changes.
  const element = useRef<HTMLButtonElement>(null);
  const idleWidth = useRef(0);
  // Taken on the click that starts loading.
  const loadingStart = useRef("0s");
  useLayoutEffect(() => {
    const el = element.current;
    if (!isMagic(variant) || !el) return;
    if (!magicLoading) {
      idleWidth.current = el.offsetWidth;
      return;
    }
    el.style.setProperty("--magic-loading-delay", loadingStart.current);
    // min-width: w-fit is !important, so an inline width loses to it. Full width ones keep their size anyway.
    if (!stretch && idleWidth.current)
      el.style.minWidth = `${idleWidth.current}px`;
    return () => {
      el.style.minWidth = "";
      el.style.removeProperty("--magic-loading-delay");
    };
  });
  // No children -> render the icon-only "IconButton" variant.
  const isIconButton = children == null;

  if (isIconButton) {
    return (
      <button
        className={cn(
          "flex shrink-0 items-center justify-center font-medium",
          disabled
            ? variant === "subtle"
              ? subtleDisabledClasses
              : disabledClasses
            : iconButtonVariantClasses[variant],
          iconButtonSizeClasses[size],
          "rounded-full",
          className,
        )}
        disabled={disabled}
        data-selected={selected}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
        onClick={onClick}
        {...props}
      >
        {icon && (
          <span
            className={cn(
              "flex shrink-0 items-center justify-center",
              iconSizeClasses[size],
            )}
          >
            {icon}
          </span>
        )}
      </button>
    );
  }

  return (
    <button
      ref={element}
      className={buttonClasses({
        variant,
        size,
        iconPosition,
        stretch,
        disabled: (disabled && !magicLoading) || magicOff,
        className: cn(magicLoading && "magic-loading", className),
      })}
      disabled={disabled || magicLoading}
      aria-busy={magicLoading || undefined}
      data-selected={selected}
      onClick={(e) => {
        if (magic) loadingStart.current = loadingDelay(e.currentTarget);
        onClick?.(e);
      }}
      onPointerMove={(e) => {
        magic?.move(e);
        onPointerMove?.(e);
      }}
      onPointerLeave={(e) => {
        magic?.leave(e);
        onPointerLeave?.(e);
      }}
      {...props}
    >
      {icon && (
        <span
          className={cn(
            "flex shrink-0 items-center justify-center",
            iconSizeClasses[size],
            iconPosition === "left" ? "mr-1" : "ml-1",
          )}
        >
          {icon}
        </span>
      )}
      {/* Plain text gets its own element: browser translation swaps bare text
          nodes out from under React, so an icon mounting before them
          (insertBefore) would crash the page. */}
      {typeof children === "string" || typeof children === "number" ? (
        <span>{children}</span>
      ) : (
        children
      )}
    </button>
  );
};

interface ChipButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  selected?: boolean;
  /*
    Soft-disable: greys the chip and no-ops the click, but stays hoverable so a
    wrapping Tooltip still fires (a native `disabled` button swallows hover).
  */
  disabled?: boolean;
  className?: string;
}

export const ChipButton = ({
  onClick,
  children,
  selected,
  disabled,
  className,
}: ChipButtonProps) => {
  return (
    <Button
      variant="custom"
      size="small"
      className={cn(
        "text-foreground-muted",
        "[--border-gradient-fill:transparent]",
        "[--border-gradient-from:transparent]",
        "[--border-gradient-to:transparent]",
        disabled
          ? "text-on-disabled cursor-not-allowed"
          : "not-data-[selected=true]:hover:text-white",
        className,
        // "data-[selected=true]:[--border-gradient-from:var(--color-amber-400)]",
        // "data-[selected=true]:[--border-gradient-to:var(--color-amber-900)]",
      )}
      selected={selected}
      onClick={disabled ? () => {} : onClick}
    >
      {children}
    </Button>
  );
};
